// WCAG 2.x relative luminance and contrast helpers for concierge theming.
// Single source for Admin (write-time validation, preview) and Marketplace
// (scheme derivation at render). Mirrors the server-side Ruby implementation
// in Armageddon so client checks agree with write-time validation.

export type ConciergeScheme = 'DARK' | 'LIGHT';

// Canvas bounds that keep the concierge's fixed text inks legible against
// seed-derived surfaces. The bands don't overlap, so every valid canvas
// belongs to exactly one scheme — which is why the scheme is derived from
// the canvas rather than stored.
//
// These MUST equal DARK_CANVAS_MAX_LUMINANCE / LIGHT_CANVAS_MIN_LUMINANCE in
// Armageddon's WebCustomizationSettingsV2::Luminance — the shared boundary-gray
// fixtures (#5d5d5d/#5e5e5e and #cbcbcb/#cccccc) are pinned in both suites so
// a one-sided change fails a test on either side.
export const DARK_CANVAS_MAX_LUMINANCE = 0.11;
export const LIGHT_CANVAS_MIN_LUMINANCE = 0.6;

// WCAG 1.4.11 non-text minimum — the floor for the accent against the canvas.
export const ACCENT_MIN_CONTRAST_RATIO = 3;

// 6-digit hex is the only stored theme color format — stricter than CSS on
// purpose, because these values are interpolated into style sheets and
// anything else must be dropped, never rendered.
const THEME_HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function isValidThemeHex(value: string | null | undefined): value is string {
  return typeof value === 'string' && THEME_HEX_PATTERN.test(value);
}

// Channel-level primitive for call sites whose colors arrive as computed
// rgb() component values (0–255) rather than stored hexes.
export function relativeLuminanceFromRgb([r, g, b]: [number, number, number]): number {
  const [lr, lg, lb] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return relativeLuminanceFromRgb([r, g, b]);
}

// Validation never lets a mid-band canvas persist, so any stored canvas
// classifies unambiguously; the 0.5 midpoint is only a tiebreak for legacy
// or externally-written data.
export function deriveConciergeScheme(hex: string): ConciergeScheme {
  return relativeLuminance(hex) >= 0.5 ? 'LIGHT' : 'DARK';
}

export function isCanvasInRange(hex: string, scheme: ConciergeScheme): boolean {
  const luminance = relativeLuminance(hex);
  return scheme === 'LIGHT' ? luminance >= LIGHT_CANVAS_MIN_LUMINANCE : luminance <= DARK_CANVAS_MAX_LUMINANCE;
}

// A canvas is storable if it renders legibly in at least one scheme — i.e.
// it falls inside one of the two luminance bands. Mid-range colors fit
// neither and are rejected.
export function isCanvasSelectable(hex: string): boolean {
  return isCanvasInRange(hex, 'DARK') || isCanvasInRange(hex, 'LIGHT');
}

export function contrastRatioFromLuminances(luminanceA: number, luminanceB: number): number {
  const [hi, lo] = luminanceA > luminanceB ? [luminanceA, luminanceB] : [luminanceB, luminanceA];
  return (hi + 0.05) / (lo + 0.05);
}

export function contrastRatio(hexA: string, hexB: string): number {
  return contrastRatioFromLuminances(relativeLuminance(hexA), relativeLuminance(hexB));
}

// The accent must sit on the far side of the canvas — lighter than a dark
// canvas, darker than a light one — by at least 3:1. The direction
// requirement is what lets one pairwise check cover both accent roles: the
// fixed on-accent label ink (near-black in the dark scheme, white in the
// light scheme) is always at least as far from the accent as the canvas is,
// so accent-as-fill clears the same ratio accent-as-ink does.
export function isAccentReadable(accentHex: string, canvasHex: string): boolean {
  if (contrastRatio(accentHex, canvasHex) < ACCENT_MIN_CONTRAST_RATIO) {
    return false;
  }

  const accentIsLighter = relativeLuminance(accentHex) > relativeLuminance(canvasHex);
  return deriveConciergeScheme(canvasHex) === 'DARK' ? accentIsLighter : !accentIsLighter;
}
